import React, { useContext, useState, useEffect } from 'react';
import { FieldData, TabsMultiFieldType } from 'types/FieldTypes';
import FormFieldContext from '../FormFieldContext';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import FieldWrapper from '../FieldWrapper';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';
import '../../../style/scss/tooltip.scss';

const TabsMultiField: React.FC<TabsMultiFieldType & { data?: FieldData, viewKey: string }> = ({  
    viewKey, name, label, data, defaultValue = [], helperText, tooltipText, hardcodedOptions, viewInEditMode,
    buttonSize = 'normal', disabled, alignment, isEditable, fullWidth = false, customClass
}) => {
    const { t } = useTranslation();
    const { editing, updatedData, setUpdatedData } = useContext(FormFieldContext);
    const { setUnsavedChanges } = useGlobalContext();
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    // Set the current values if they exist, checking updatedData first so a
    // field hidden and shown again by dependentOn keeps a value the user
    // already chose instead of reapplying the default on every remount
    useEffect(() => {
        if (data && Array.isArray(data[name])) {
            setSelectedValues(data[name]);
        } else if (Array.isArray(updatedData?.[name])) {
            setSelectedValues(updatedData[name]);
        } else if (defaultValue.length > 0) {
            setSelectedValues(defaultValue);
            // Use the functional updater, a sibling field applying its own
            // default in the same render batch would otherwise clobber this
            setUpdatedData(previous => ({ ...previous, [name]: defaultValue }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle the tabs change
    const handleTabsChange = (value: string) => {
        let updated: string[];
        if (selectedValues.includes(value)) {
            updated = selectedValues.filter(v => v !== value);
        } else {
            updated = [...selectedValues, value];
        }

        setSelectedValues(updated);

        // Get the current data values and check differences
        const current: string[] = Array.isArray(data?.[name]) ? data![name] : [];
        const isSame =
            current.length === updated.length &&
            current.every((val: string) => updated.includes(val));

        setUnsavedChanges(viewKey, !isSame);
        setUpdatedData({...updatedData, [name]: updated });
    };

    return (
        <FieldWrapper
            name={name}
            label={label}
            tooltipText={tooltipText}
            helperText={helperText}
            isEditable={isEditable}
            disabled={disabled}
            viewInEditMode={viewInEditMode}
            alignment={alignment}>
            {(editing || viewInEditMode) && (
                // Edit mode
                <div className={`tabs-field ${buttonSize}`}>
                    {hardcodedOptions.map((option: { value: string, name: string }, index: number) => {

                        // Determine if the option is active
                        const isActive = selectedValues.includes(option.value)
                        
                        return (
                            <button 
                                key={index}
                                className={`${customClass ? customClass : ''} ${option.value} ${isActive ? 'active' : ''} ${fullWidth ? 'stretched' : ''}`}
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleTabsChange(option.value);
                                }}
                            >
                                {t(option.name)}
                            </button>
                        );
                    })}
                </div>
            )}
        </FieldWrapper>
    );
};

export default TabsMultiField;